import * as React from 'react';
import styled from 'styled-components';
import ReactInputMask from 'react-input-mask';
import Tooltip from '@material-ui/core/Tooltip';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers';
import {
  ErrorMessages,
  CheckboxWithLabel,
  InputTextWithLabel,
  InputBaseContainer,
  InputSelectWithLabel,
  InputAutocompletePlaces,
  ButtonWithLoader,
} from '../molecules';
import { FormServerError, SelectOption } from '../types';
import { US_STATES } from '../constants/usStates';
import isYupFieldRequired from '../utils/isYupFieldRequired';
import transformServerErrorsToForm from '../utils/transformServerErrorsToForm';
import {
  USER_TYPES,
  CLIENT_TYPES,
  USER_TYPES_VALUES,
  CLIENT_TYPES_VALUES,
  SUPER_ADMIN_ROLE,
  REPORTS_ACCESS_LEVELS,
} from '../constants/forms';
import { getTrades, TradeType } from '../api/trades';
import {
  getUsers,
  getCompanyById,
  getUserByID,
  getPermitUsers,
  UserTableType,
  UserType,
} from '../api/users';
import { getCurrentUser, checkAvailableEmail } from '../api/userSettings';
import { getFranchiseList } from '../api/franchise';
import { GET_ALL_QUERY_PARAMS } from '../constants';
import { checkValidStateName } from '../utils/parseGoogleApiAddress';
import useUserRole from '../hooks/useUserRole';
import {
  labels,
  schema,
  SaFaSchema,
} from './validation/usersFormValidation';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../redux/root';

const NO_COMPANY = {
  companyName: 'No Company',
  id: '~PLACEHOLDER~',
};

const UsersForm = ({
  searchForm,
  onSubmit,
  disabledRole = false,
  isReadOnly,
  clientTypeChange,
  initialValues,
  modalError,
  addButtonText = 'Add User',
  updateButtonText = 'Update User',
  initialErrors,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setIsContactFormSubmitting = data => {},
  cameFromSearch = false,
  lockClientType = false,
  secondarySubmitButton,
  submitHelperText,
  companyMode = false,
  onBack,
  hideRoleAndType = false,
  hideParentCompany = false,
  beforeSubmit,
}: Props) => {
  const submitTargetRef = React.useRef<'primary' | 'secondary'>('primary');
  const {
    isSuperAdmin,
    isEmployee,
    isFranchiseAdmin,
  } = useUserRole();

  const {
    register,
    handleSubmit,
    control,
    errors,
    setError,
    setValue,
    watch,
    formState,
    getValues,
  } = useForm<UserTableType>({
    resolver: yupResolver(
      isSuperAdmin || isFranchiseAdmin
        ? SaFaSchema
        : schema,
    ),
    defaultValues: initialValues
      ? {
          ...initialValues,
          userRole: initialValues?.isEpsuperadmin
            ? USER_TYPES.find(
                item => item.value === SUPER_ADMIN_ROLE,
              )
            : USER_TYPES.find(
                item =>
                  item.value ===
                  (initialValues.userRole as number),
              ),
          clientType: CLIENT_TYPES.filter(
            item =>
              item.value ===
              (initialValues.clientType as number),
          )[0],
          parentCompany:
            initialValues.id && initialValues.parentCompany
              ? undefined
              : !initialValues.id &&
                initialValues.parentCompany &&
                typeof initialValues.parentCompany === 'object'
              ? (initialValues.parentCompany as any)
              : (NO_COMPANY as any),
          franchise: undefined,
          trade: undefined,
        }
      : ({} as UserTableType),
  });

  React.useEffect(() => {
    const roleValue = getValues('userRole');
    const franchiseValue = getValues('franchise');
    if (
      roleValue &&
      // @ts-ignore
      roleValue?.value !== USER_TYPES_VALUES.EMPLOYEE
    ) {
      if (
        franchiseValue &&
        !Array.isArray(franchiseValue) &&
        Object.keys(franchiseValue).length
      ) {
        // @ts-ignore
        setValue('franchise', [
          {
            // @ts-ignore
            id: franchiseValue.value || franchiseValue.id,
            // @ts-ignore
            name: franchiseValue.name,
          } as any,
        ]);
      }
    }
  }, [formState.isDirty, formState]);

  const isDeleted = initialValues?.isDeleted;

  const [isSubmitting, setIsSubmitting] = React.useState<
    boolean
  >(false);

  const [emailBlurError, setEmailBlurError] = React.useState('');
  const lastCheckedEmailRef = React.useRef<string>('');
  const initialEmailLower = React.useMemo(
    () => (initialValues?.email || '').trim().toLowerCase(),
    [initialValues],
  );

  const validateEmail = async (rawValue: string): Promise<string> => {
    const value = (rawValue || '').trim().toLowerCase();
    if (value && value !== rawValue) {
      setValue('email', value);
    }
    if (!value) {
      setEmailBlurError('');
      lastCheckedEmailRef.current = '';
      return '';
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) {
      const msg = 'Please enter a valid email address';
      setEmailBlurError(msg);
      lastCheckedEmailRef.current = '';
      return msg;
    }
    setEmailBlurError('');

    // Skip when editing and the email hasn't changed from the saved value.
    if (initialEmailLower && value === initialEmailLower) {
      lastCheckedEmailRef.current = value;
      return '';
    }

    try {
      const result = await checkAvailableEmail(value);
      // Guard against a stale response if the user edited and re-blurred.
      if (lastCheckedEmailRef.current !== value) {
        lastCheckedEmailRef.current = value;
      }
      if (result && result.email_exists) {
        const msg = 'This email is already in use';
        setEmailBlurError(msg);
        return msg;
      }
      return '';
    } catch {
      // Network errors shouldn't block submission; user will see server-side error if any.
      return '';
    }
  };

  React.useEffect(() => {
    if (
      formState.isSubmitting &&
      !Object.keys(errors).length
    ) {
      setIsContactFormSubmitting(true);
      setIsSubmitting(formState.isSubmitting);
    }
  }, [formState]);
React.useEffect(()=>{
  clientTypeChange && clientTypeChange(getValues("clientType"))
},[getValues("clientType")])
  const userRole = watch('userRole') as SelectOption;
  const clientType = watch('clientType') as SelectOption;
  // EA-1381 Reporting Access: staff roles only (SAM / BAM / EMM / IMM), set by Super Admin
  const isStaffRole = (role?: SelectOption) =>
    !!role && role.value !== '' && String(role.value) !== String(USER_TYPES_VALUES.CLIENT);
  const showReportsAccess = isSuperAdmin && isStaffRole(userRole);
  // Set placeholder values for Company clients so hidden required fields pass validation
  React.useEffect(() => {
    const isCompany = userRole?.value === USER_TYPES_VALUES.CLIENT &&
      String(clientType?.value) === String(CLIENT_TYPES_VALUES.COMPANY);
    if (isCompany) {
      setValue('firstName', 'N/A');
      setValue('lastName', 'N/A');
    } else {
      if (getValues('firstName') === 'N/A') setValue('firstName', '');
      if (getValues('lastName') === 'N/A') setValue('lastName', '');
    }
  }, [clientType]);
  const selectedFranchise = watch('franchise')
  const franchiseValue = useSelector((s:RootState) => s.userAuth.info.selectedFranchise);
  const [isLoading, setIsLoading] = React.useState(false);
  const [
    franchisesData,
    setFranchisesData,
  ] = React.useState([]);
  const [tradesData, setTradesData] = React.useState([]);
  const [
    parentCompanyData,
    setParentCompanyData,
  ] = React.useState([NO_COMPANY as any]);
  const [
    isParentCompanyLoading,
    setIsParentCompanyLoading,
  ] = React.useState<boolean>(
    !!(initialValues?.id && initialValues?.parentCompany),
  );
  const parentSearchTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const parentLastSearchRef = React.useRef<string>('');

  const handleParentCompanyInputChange = (input: string, action?: string) => {
    if (action !== 'input-change') return;
    if (parentSearchTimeoutRef.current) {
      clearTimeout(parentSearchTimeoutRef.current);
    }
    if (input.length < 3) return;
    parentSearchTimeoutRef.current = setTimeout(() => {
      parentLastSearchRef.current = input;
      setIsParentCompanyLoading(true);
      getPermitUsers({
        search: input,
        limit: 25,
        offset: 0,
        userRole: USER_TYPES_VALUES.CLIENT,
        clientType: CLIENT_TYPES_VALUES.COMPANY,
      } as any)
        .then((res: any) => {
          if (parentLastSearchRef.current !== input) return;
          const results = (res?.data?.results || res?.data || []) as any[];
          setParentCompanyData([NO_COMPANY as any, ...results]);
        })
        .catch(() => {})
        .finally(() => {
          setIsParentCompanyLoading(false);
        });
    }, 300);
  };

  React.useEffect(() => {
    setIsLoading(true);

    getFranchiseList({
      ...GET_ALL_QUERY_PARAMS,
      ordering: 'name',
    })
      .then(franchisesResponse => {
        setFranchisesData(franchisesResponse.data.results);
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));

    if (!lockClientType) {
      getTrades({
        ...GET_ALL_QUERY_PARAMS,
        ordering: 'name',
      })
        .then(tradesResponse => {
          setTradesData(tradesResponse.data.results);
        })
        .catch(() => {});

      getUsers({
        ...GET_ALL_QUERY_PARAMS,
        clientType: CLIENT_TYPES_VALUES.COMPANY,
      })
        .then(usersResponse => {
          setParentCompanyData(prev => [
            ...prev,
            ...usersResponse.data.results,
          ]);
        })
        .catch(() => {})
        .finally(() => {
          setIsParentCompanyLoading(false);
        });
    } else {
      setIsParentCompanyLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (isEmployee) {
      setValue(
        'userRole',
        USER_TYPES?.find(
          el => {
              return el?.value === USER_TYPES_VALUES.CLIENT
          },
        ),
      );
      getCurrentUser()
        .then(res =>
          setValue('franchise', res.data.franchise))
        .catch(err => console.log(err));
    }
  }, [isEmployee]);

  useEffect(() => {
    if(initialValues && initialValues.defaultFranchiseId ){
      if(franchisesData && franchisesData.length > 0) {
        const defaultFran = franchisesData.find(f => f.id === initialValues.defaultFranchiseId);
        if(defaultFran){
          setValue('defaultFranchise', defaultFran);
        }
      }
    }
  },[initialValues,franchisesData])
  React.useEffect(() => {
    if (
      !initialValues?.id &&
      initialValues?.parentCompany &&
      typeof initialValues.parentCompany === 'object' &&
      (initialValues.parentCompany as any).id !== NO_COMPANY.id
    ) {
      setValue('parentCompany', initialValues.parentCompany as any);
    }

    const parentRef = initialValues?.parentCompany;
    if (
      initialValues?.id &&
      typeof parentRef === 'number' &&
      parentRef > 0
    ) {
      // The contact payload already carries the parent company's resolved name
      // (the backend denormalizes it into `companyName`), so seed the select
      // from that instead of re-fetching. This is the same name the Contacts
      // table shows and avoids a network round-trip — critically, it avoids the
      // 404 that fired when `parent_company` referenced an id that isn't in the
      // /account/companies/ (UsersCompany) table.
      const seededName = (initialValues as any)?.companyName;
      if (seededName) {
        setValue('parentCompany', {
          id: parentRef,
          companyName: seededName,
        } as any);
        setIsParentCompanyLoading(false);
      } else {
        // No denormalized name available — fall back to resolving it by id.
        // `parent_company` doesn't always live in the /account/companies/
        // (UsersCompany) table — the "Create new company" and company-name
        // search paths store a client_type=COMPANY User id instead, which is a
        // different id space — so try companies first, then users.
        getCompanyById(parentRef)
          .then(res => {
            setValue('parentCompany', res.data as any);
            setIsParentCompanyLoading(false);
          })
          .catch(() => {
            getUserByID(parentRef)
              .then(res => {
                const company = res.data as any;
                if (company?.companyName) {
                  setValue('parentCompany', {
                    id: company.id,
                    companyName: company.companyName,
                  } as any);
                }
                setIsParentCompanyLoading(false);
              })
              .catch(() => {
                // Fall back to full-list match in the other useEffect.
              });
          });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (initialValues?.id) {
      if (franchisesData.length && initialValues.franchise) {
        const selectedFranchises = franchisesData.filter(
          item => initialValues.franchise.includes(item.id),
        );

        if (selectedFranchises.length) {
          if (selectedFranchises.length === 1) {
            setValue('franchise', {
              // @ts-ignore
              id: selectedFranchises[0].id,
              name: selectedFranchises[0].name,
            });
          } else {
            setValue('franchise', selectedFranchises);
          }
        }
      }

      if (tradesData.length && initialValues.trade) {
        const selectedTrades = tradesData.filter(item =>
          initialValues.trade.includes(item.id),
        );

        if (selectedTrades.length) {
          setValue('trade', selectedTrades);
        }
      }

      if (parentCompanyData.length) {
        const selectedCompany = parentCompanyData.find(
          item => item.id === initialValues.parentCompany || item.companyName === initialValues.parentCompany,
        );

        if (selectedCompany) {
          setValue('parentCompany', selectedCompany);
        }
      }
    }
  }, [franchisesData, tradesData, parentCompanyData]);

  React.useEffect(() => {
    if (initialErrors) {
      transformServerErrorsToForm<UserType>(
        initialErrors,
      ).forEach(({ name, types }) =>
        setError(name, { types }),
      );
    }
  }, [initialErrors]);

  React.useEffect(() => {
    if (Object.keys(errors).length) {
      setIsSubmitting(false);
    }
  }, [errors, initialErrors]);

  const handlePlaceSelect = (address, isSelect) => {
    if (isSelect) {
      return Object.entries(address).map(([key, value]) => {
        if (key === 'state') {
          const match = US_STATES.find(
            s =>
              s.label.toLowerCase() === String(value).toLowerCase() ||
              s.value.toLowerCase() === String(value).toLowerCase(),
          );
          return setValue(key, match?.value || value);
        }
        return setValue(key, value);
      });
    }

    return setValue('address', address);
  };

  const middleware = (
    submitFn: (data: UserType) => void,
  ) => async (data: UserTableType) => {
    const selectedFranchises = franchisesData.filter(item =>
      initialValues?.franchise?.includes(item.id),
    );
    const userRoleValue = (data.userRole as SelectOption)
      .value;
    const clientTypeValue =
      (data.clientType as SelectOption)?.value ||
      CLIENT_TYPES_VALUES.NOT_CLIENT;

    const companyName =
      (data.clientType as SelectOption)?.value ===
      String(CLIENT_TYPES_VALUES.COMPANY)
        ? data?.companyName
        : '';
   const isCompanyType = String(clientTypeValue) === String(CLIENT_TYPES_VALUES.COMPANY);
   const finalData = {
      ...data,
      email: (data.email || '').trim().toLowerCase(),
      defaultFranchiseId:data.defaultFranchise.id,
      firstName: isCompanyType ? '' : data.firstName,
      lastName: isCompanyType ? '' : data.lastName,
      id: initialValues?.id,
      userRole:
        userRoleValue === SUPER_ADMIN_ROLE
          ? USER_TYPES_VALUES.FRANCHISE_ADMIN
          : userRoleValue,
      clientType:
        userRoleValue === String(USER_TYPES_VALUES.CLIENT)
          ? clientTypeValue
          : CLIENT_TYPES_VALUES.NOT_CLIENT,
      parentCompany:
        (data.parentCompany as any)?.id === NO_COMPANY.id
          ? null
          : (data.parentCompany as UserType)?.id,
      franchise:
        userRoleValue === SUPER_ADMIN_ROLE
          ? []
          : Array.isArray(data.franchise)
          ? data.franchise.map(f => f.id)
          : [data.franchise.id],
      trade: (data.trade as TradeType[])?.map(
        item => item.id,
      ),
      isEpsuperadmin: userRoleValue === SUPER_ADMIN_ROLE,
      companyName,
    };
   if (!finalData.password) {
     delete finalData.password;
   }
   delete (finalData as any).reportsAccessLevel;
   if (isSuperAdmin) {
     (finalData as any).reportsAccessLevelId = isStaffRole(data.userRole as SelectOption)
       ? ((data as any).reportsAccessLevel as SelectOption)?.value ?? null
       : null;
   }
   if (submitTargetRef.current === 'secondary' && secondarySubmitButton) {
     submitTargetRef.current = 'primary';
     return secondarySubmitButton.onClick(finalData as UserType);
   }
   return submitFn(finalData);
  };

  const isFranchiseMultiSelect = () => {
    // removing super admin check -- EA-657 - mritunjoy
    if (isFranchiseAdmin) {
      if (userRole?.value === USER_TYPES_VALUES.EMPLOYEE) {
        return false;
      }
    }
    return true;
  };

  return (
    <Container
      disabled={isDeleted || (isSubmitting && !modalError)}
    >
      <FormContainer
        $readOnly={isReadOnly}
        autoComplete="off"
        aria-autocomplete="none"
        onSubmit={async e => {
          if (emailBlurError) {
            e.preventDefault();
            return;
          }
          // Re-check on submit if blur was skipped (e.g. user pasted + clicked Save).
          const currentEmail = getValues('email');
          if (currentEmail || beforeSubmit) {
            e.preventDefault();
            if (currentEmail) {
              const err = await validateEmail(currentEmail);
              if (err) return;
            }
            // Runs BEFORE handleSubmit so an abort never strands isSubmitting.
            if (
              beforeSubmit &&
              !(await beforeSubmit(getValues() as UserTableType))
            ) {
              return;
            }
            handleSubmit(middleware(onSubmit))();
            return;
          }
          handleSubmit(middleware(onSubmit))(e);
        }}
      >
        <TwoCol>
        <Col>
        <InputBaseContainer hidden={lockClientType || hideRoleAndType}>
          <Controller
            name="userRole"
            defaultValue=""
            control={control}
            render={props => (
              <InputSelectWithLabel
                placeholder=""
                label={labels.userRole}
                error={errors.userRole}
                isRequired={isYupFieldRequired(
                  'userRole',
                  schema,
                )}
                isDisabled={((disabledRole && initialValues) || cameFromSearch) || isReadOnly || (isFranchiseAdmin && !!initialValues?.id)}
                isSearchable={false}
                isOptionDisabled={option =>
                  (!isSuperAdmin &&
                    (option.label === 'Super admin' ||
                      option.label ===
                        'Branch admin')) ||
                  (isEmployee &&
                    (option.label === 'Super admin' ||
                      option.label === 'Branch admin' ||
                      option.label === 'Inspector' ||
                      option.label === 'Employee'))
                }
                options={USER_TYPES}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
              />
            )}
          />
        </InputBaseContainer>
        <InputBaseContainer
          hidden={
            lockClientType ||
            hideRoleAndType ||
            userRole?.value !== USER_TYPES_VALUES.CLIENT
          }
        >
          <Controller
            name="clientType"
            defaultValue=""
            control={control}
            render={props => (
              <InputSelectWithLabel
                isRequired
                isDisabled={isReadOnly || cameFromSearch || lockClientType || (isFranchiseAdmin && !!initialValues?.id)}
                placeholder=""
                isSearchable={false}
                label={labels.clientType}
                error={errors.clientType}
                options={CLIENT_TYPES}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
              />
            )}
          />
        </InputBaseContainer>
        <InputBaseContainer
          hidden={
            !(
              userRole?.value ===
                USER_TYPES_VALUES.CLIENT &&
              clientType?.value ===
                CLIENT_TYPES_VALUES.COMPANY
            )
          }
        >
          <InputTextWithLabel
            name="companyName"
            isDisabled={isReadOnly}
            inputRef={register}
            error={errors.companyName}
            label={labels.companyName}
            isRequired={
              isYupFieldRequired('companyName', schema) ||
              (userRole?.value ===
                USER_TYPES_VALUES.CLIENT &&
                clientType?.value ===
                  CLIENT_TYPES_VALUES.COMPANY)
            }
            disabled={cameFromSearch}
          />
        </InputBaseContainer>
        <InputBaseContainer
          hidden={
            hideParentCompany ||
            userRole?.value !== USER_TYPES_VALUES.CLIENT ||
            !clientType ||
            clientType?.value ===
              CLIENT_TYPES_VALUES.COMPANY
          }
        >
          <Controller
            name="parentCompany"
            defaultValue={NO_COMPANY}
            control={control}
            render={props => (
              <InputSelectWithLabel
                placeholder={
                  isParentCompanyLoading
                    ? 'Loading…'
                    : 'Type 3+ characters to search'
                }
                isDisabled={isReadOnly || cameFromSearch || lockClientType || (!!initialValues?.id && !isSuperAdmin)}
                isLoading={isParentCompanyLoading}
                label={
                  <LabelWithHint>
                    {labels.parentCompany}
                    {/* Tooltip hidden while parent-company select is disabled in edit mode.
                    <Tooltip
                      title="Start typing 3 or more characters to search for a different company."
                      placement="top"
                      arrow
                    >
                      <HintIconWrap tabIndex={0} aria-label="Search hint">
                        <InfoOutlinedIcon style={{ fontSize: 14 }} />
                      </HintIconWrap>
                    </Tooltip>
                    */}
                  </LabelWithHint>
                }
                error={errors.parentCompany}
                getOptionValue={(item: any) =>
                  item.id || Math.random()
                }
                getOptionLabel={(item: any) =>
                  item.companyName
                }
                isRequired={isYupFieldRequired(
                  'parentCompany',
                  schema,
                )}
                isSearchable
                filterOption={() => true}
                onInputChange={(value: string, meta: any) =>
                  handleParentCompanyInputChange(value, meta?.action)
                }
                options={parentCompanyData}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
              />
            )}
          />
        </InputBaseContainer>
        <InputBaseContainer
          hidden={
            userRole?.value === USER_TYPES_VALUES.CLIENT &&
            clientType?.value === CLIENT_TYPES_VALUES.COMPANY
          }
        >
          <InputTextWithLabel
            name="firstName"
            disabled={isReadOnly}
            inputRef={register}
            error={errors.firstName}
            label={labels.firstName}
            isRequired
            disabled={cameFromSearch}
          />
          <InputTextWithLabel
            name="lastName"
            disabled={isReadOnly}
            inputRef={register}
            error={errors.lastName}
            label={labels.lastName}
            isRequired
            disabled={cameFromSearch}
          />
        </InputBaseContainer>
        <InputBaseContainer>
          <InputTextWithLabel
            name="email"
            type="email"
            disabled={isReadOnly || cameFromSearch}
            error={errors.email || (emailBlurError ? { message: emailBlurError } : undefined)}
            inputRef={register}
            label={companyMode ? 'Company Email' : labels.email}
            isRequired={
              !(userRole?.value === USER_TYPES_VALUES.CLIENT &&
                clientType?.value === CLIENT_TYPES_VALUES.COMPANY)
            }
            placeholder={companyMode ? 'company@example.com' : 'user@example.com'}
            onBlur={e => validateEmail(e.target.value)}
            onChange={e => {
              if (emailBlurError) setEmailBlurError('');
            }}
          />
          <Controller
            control={control}
            defaultValue=""
            name="phoneNumber"
            render={props => (
              <ReactInputMask
                mask="999-999-9999"
                disabled={isReadOnly}
                maskChar=" "
                disabled={cameFromSearch}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
              >
                {() => (
                  <InputTextWithLabel style={isReadOnly && {backgroundColor:"#f2f2f2",opacity:"0.7"}}
                    error={errors.phoneNumber}
                    label={companyMode ? 'Company Phone' : labels.phoneNumber}
                    isRequired={isYupFieldRequired(
                      'phoneNumber',
                      schema,
                    ) || (userRole?.label !== 'Client')}
                    style={cameFromSearch ? {backgroundColor: '#F2F2F2', opacity: 0.7} : {}}
                  />
                )}
              </ReactInputMask>
            )}
          />
        </InputBaseContainer>
        {userRole?.label === 'Client' &&
        clientType?.label === 'Individual' ? (
          <InputBaseContainer
            hidden={
              userRole?.value !==
                USER_TYPES_VALUES.CLIENT ||
              !clientType ||
              clientType?.value ===
                CLIENT_TYPES_VALUES.COMPANY
            }
          >
            <Controller
              control={control}
              defaultValue=""
              name="mobilePhoneNumber"
              render={props => (
                <ReactInputMask
                  mask="999-999-9999"
                  disabled={isReadOnly}
                  maskChar=" "
                  disabled={cameFromSearch}
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...props}
                >
                  {() => (
                    <InputTextWithLabel style={isReadOnly && { backgroundColor: '#f2f2f2', opacity: '0.7' }}
                      error={errors.mobilePhoneNumber}
                      label={labels.mobilePhoneNumber}
                      isRequired={
                        isYupFieldRequired(
                          'mobilePhoneNumber',
                          schema,
                        ) || (userRole?.label !== 'Client')
                      }
                      style={cameFromSearch ? { backgroundColor: '#F2F2F2', opacity: 0.7 } : {}}
                    />
                  )}
                </ReactInputMask>
              )}
            />
          </InputBaseContainer>
        ) : null}
        {showReportsAccess && (
          <InputBaseContainer>
            <Controller
              name="reportsAccessLevel"
              control={control}
              defaultValue={
                REPORTS_ACCESS_LEVELS.find(
                  item => item.value === initialValues?.reportsAccessLevelId,
                ) || null
              }
              render={props => (
                <InputSelectWithLabel
                  placeholder="No access"
                  label={labels.reportsAccessLevel}
                  isSearchable={false}
                  isClearable
                  isDisabled={isReadOnly || cameFromSearch}
                  options={REPORTS_ACCESS_LEVELS}
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...props}
                />
              )}
            />
          </InputBaseContainer>
        )}
        </Col>
        <Col>
        <InputBaseContainer>
          <Controller
            name="address"
            defaultValue=""
            control={control}
            render={props => (
              <InputAutocompletePlaces
              isDisabled={isReadOnly}
                error={errors.address}
                label={companyMode ? 'Company Address' : labels.address}
                handleFieldChange={handlePlaceSelect}
                isRequired={isYupFieldRequired(
                  'address',
                  schema,
                )}
                isDisabled={cameFromSearch}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
              />
            )}
          />
        </InputBaseContainer>
        <InputBaseContainer>
          <InputTextWithLabel
           disabled={isReadOnly}
            name="street"
            error={errors.street}
            inputRef={register}
            label={labels.street}
            isRequired={isYupFieldRequired(
              'street',
              schema,
            )}
            disabled={cameFromSearch}
          />
          <InputTextWithLabel
            name="city"
            disabled={isReadOnly}
            error={errors.city}
            inputRef={register}
            label={labels.city}
            isRequired={isYupFieldRequired('city', schema)}
            disabled={cameFromSearch}
          />
        </InputBaseContainer>
        <InputBaseContainer>
          <Controller
            name="state"
            defaultValue=""
            control={control}
            render={({ onChange, onBlur, value }) => {
              const selected = US_STATES.find(
                s => s.value === value || s.label === value,
              ) || null;
              return (
                <InputSelectWithLabel
                  placeholder="Select State"
                  label={labels.state}
                  error={errors.state}
                  isRequired={isYupFieldRequired(
                    'state',
                    schema,
                  )}
                  isSearchable
                  isDisabled={isReadOnly || cameFromSearch}
                  options={US_STATES}
                  value={selected}
                  onBlur={onBlur}
                  onChange={opt => onChange(opt?.value || '')}
                />
              );
            }}
          />
          <Controller
            name="zipCode"
            defaultValue=""
            control={control}
            render={({ onChange, onBlur, value }) => (
              <InputTextWithLabel
                name="zipCode"
                label={labels.zipCode}
                error={errors.zipCode}
                isRequired={isYupFieldRequired(
                  'zipCode',
                  schema,
                )}
                disabled={isReadOnly || cameFromSearch}
                value={value}
                onBlur={onBlur}
                onChange={e => {
                  const digits = e.target.value.replace(/[^0-9]/g, '').slice(0, 9);
                  let formatted = digits;
                  if (digits.length > 5) {
                    formatted = `${digits.slice(0, 5)}-${digits.slice(5)}`;
                  }
                  onChange(formatted);
                }}
                inputProps={{
                  inputMode: 'numeric',
                  maxLength: 10,
                }}
                placeholder="55555 or 55555-5555"
              />
            )}
          />
        </InputBaseContainer>
        <InputBaseContainer
          hidden={
            userRole?.value === SUPER_ADMIN_ROLE ||
            isEmployee
          }
        >
          <Controller
            name="franchise"
            defaultValue=""
            control={control}
            render={props => (
              <InputSelectWithLabel
                isMulti={isFranchiseMultiSelect()}
                placeholder=""
                menuPlacement="top"
                isLoading={isLoading}
                isDisabled={isLoading || cameFromSearch || isReadOnly}
                label={labels.franchise}
                error={errors.franchise}
                getOptionValue={(item: any) =>
                  item.id || Math.random()
                }
                getOptionLabel={(item: any) => item.name}
                isRequired={
                  isYupFieldRequired(
                    'franchise',
                    isSuperAdmin || isFranchiseAdmin
                      ? SaFaSchema
                      : schema,
                  ) ||
                  userRole?.value !==
                    String(SUPER_ADMIN_ROLE)
                }
                options={franchisesData}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
              />
            )}
          />
        </InputBaseContainer>
        {/* Adding single select for default franchise - EA-657- mritunjoy */}
        <InputBaseContainer
          hidden={
            userRole?.value === SUPER_ADMIN_ROLE ||
            isEmployee
          }
        >
          <Controller
            name="defaultFranchise"
            defaultValue=""
            control={control}
            render={props => (
              <InputSelectWithLabel
                isMulti={false}
                placeholder=""
                menuPlacement="top"
                isLoading={isLoading}
                isDisabled={isLoading || cameFromSearch || isReadOnly}
                label={labels.defaultFranchise}
                error={errors.defaultFranchise}
                getOptionValue={(item: any) =>
                  item.id || Math.random()
                }
                getOptionLabel={(item: any) => item.name}
                isRequired={
                  isYupFieldRequired(
                    'defaultFranchise',
                    isSuperAdmin || isFranchiseAdmin
                      ? SaFaSchema
                      : schema,
                  ) ||
                  userRole?.value !==
                    String(SUPER_ADMIN_ROLE)
                }
                options={selectedFranchise ? selectedFranchise : []}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
              />
            )}
          />
        </InputBaseContainer>
        {isSuperAdmin && initialValues?.id && (
          <InputBaseContainer>
            <InputTextWithLabel
              name="password"
              type="password"
              autoComplete="new-password"
              inputRef={register}
              error={errors.password}
              label="Password"
              placeholder="Leave blank to keep current"
              disabled={isReadOnly || cameFromSearch}
            />
          </InputBaseContainer>
        )}
        </Col>
        </TwoCol>
        <InputBaseContainer
          hidden={
            userRole?.value !== USER_TYPES_VALUES.INSPECTOR
          }
        >
          <Controller
            name="trade"
            defaultValue=""
            control={control}
            render={props => (
              <InputSelectWithLabel
                isMulti
                isRequired
                placeholder=""
                isLoading={isLoading}
                isDisabled={isLoading || cameFromSearch || isReadOnly}
                menuPlacement="top"
                label={labels.trade}
                error={errors.trade}
                getOptionValue={(item: any) =>
                  item.id || Math.random()
                }
                getOptionLabel={(item: any) => item.name}
                options={tradesData}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
              />
            )}
          />
        </InputBaseContainer>
        <InputBaseContainer
          hidden={
            userRole?.value !== USER_TYPES_VALUES.INSPECTOR
          }
        >
          <InputTextWithLabel
            name="licenses"
            disabled={isReadOnly}
            inputRef={register}
            error={errors.licenses}
            label={labels.licenses}
            isRequired={isYupFieldRequired(
              'licenses',
              schema,
            )}
            disabled={cameFromSearch}
          />
        </InputBaseContainer>
        <InputBaseContainer
          hidden={
            userRole?.value !== USER_TYPES_VALUES.INSPECTOR
          }
        >
          <InputTextWithLabel
            name="boLicense"
            inputRef={register}
            error={errors.boLicense}
            label={labels.boLicense}
            isRequired={isYupFieldRequired(
              'boLicense',
              schema,
            )}
            disabled={isReadOnly || cameFromSearch}
          />
        </InputBaseContainer>
        <InputBaseContainer
          hidden={
            userRole?.value !== USER_TYPES_VALUES.INSPECTOR
          }
        >
          <InputTextWithLabel
            name="archEngLicense"
            inputRef={register}
            error={errors.archEngLicense}
            label={labels.archEngLicense}
            isRequired={isYupFieldRequired(
              'archEngLicense',
              schema,
            )}
            disabled={isReadOnly || cameFromSearch}
          />
        </InputBaseContainer>
        <InputBaseContainer
          hidden={
            userRole?.value !==
              USER_TYPES_VALUES.INSPECTOR &&
            userRole?.value !== USER_TYPES_VALUES.EMPLOYEE
          }
        >
          <InputTextWithLabel
            name="prNumber"
            disabled={isReadOnly}
            inputRef={register}
            error={errors.prNumber}
            label={labels.prNumber}
            isRequired={isYupFieldRequired(
              'prNumber',
              schema,
            )}
            disabled={cameFromSearch}
          />
        </InputBaseContainer>
        {userRole?.value === 1 && <InputBaseContainer>
          <Controller
            name="canScheduleInspections"
            control={control}
            valueName="checked"
            defaultValue={
              initialValues ? initialValues.canScheduleInspections : false
            }
            render={({ onChange, onBlur, value }) => (
              <CheckboxWithLabel
              disabled={isReadOnly}
                error={errors.canScheduleInspections}
                label={labels.canScheduleInspections}
                checked={value}
                onBlur={onBlur}
                onChange={(e, flag) => onChange(flag)}
                disabled={cameFromSearch}
              />
            )}
          />
        </InputBaseContainer>}
        <InputBaseContainer>
          <Controller
            name="isActive"
            control={control}
            valueName="checked"
            defaultValue={
              initialValues ? initialValues.isActive : true
            }
            render={({ onChange, onBlur, value }) => (
              <CheckboxWithLabel
                error={errors.isActive}
                label={labels.isActive}
                checked={value}
                onBlur={onBlur}
                onChange={(e, flag) => onChange(flag)}
                disabled={isReadOnly || cameFromSearch || ((isFranchiseAdmin || isEmployee) && !!initialValues?.id)}
              />
            )}
          />
        </InputBaseContainer>
        {errors?.parentCompany && (
          <ErrorMessages error={errors.parentCompany} />
        )}
        {userRole?.value === SUPER_ADMIN_ROLE &&
          errors?.franchise && (
            <ErrorMessages error={errors.franchise} />
          )}
        {(!isDeleted && !isReadOnly)&& (
          <>
            {submitHelperText && (
              <SubmitHelperText>{submitHelperText}</SubmitHelperText>
            )}
            <ButtonRow>
              {onBack && (
                <BackBtn
                  type="button"
                  disabled={isSubmitting && !modalError}
                  onClick={onBack}
                >
                  Back
                </BackBtn>
              )}
              <ButtonWithLoader
                disabled={((isSubmitting && !modalError) || cameFromSearch) || isReadOnly}
                loading={isSubmitting && !modalError}
                type="submit"
                onClick={() => { submitTargetRef.current = 'primary'; }}
              >
                {initialValues
                  ? updateButtonText
                  : addButtonText}
              </ButtonWithLoader>
              {secondarySubmitButton && (
                <SecondarySubmitBtn
                  type="submit"
                  disabled={(isSubmitting && !modalError) || isReadOnly}
                  onClick={() => { submitTargetRef.current = 'secondary'; }}
                >
                  {secondarySubmitButton.label}
                </SecondarySubmitBtn>
              )}
            </ButtonRow>
          </>
        )}
      </FormContainer>
    </Container>
  );
};

const Container = styled.fieldset`
  display: block;
  margin: 0;
  padding: 0;
  width: 100%;
  border: none;
  text-align: center;
`;

const FormContainer = styled.form<{ $readOnly?: boolean }>`
  width: 100%;

  .MuiButton-root {
    margin-top: 24px;
  }

  ${({ $readOnly }) =>
    $readOnly &&
    `
    input,
    textarea,
    select,
    .MuiInputBase-root,
    .MuiOutlinedInput-root,
    .MuiSelect-root,
    [class*="control"],
    [class*="Control"],
    [class*="ValueContainer"],
    [class*="IndicatorsContainer"] {
      background-color: #f2f2f2 !important;
      color: #6b6b6b !important;
      opacity: 0.85;
      cursor: not-allowed !important;
    }

    .MuiCheckbox-root,
    .MuiFormControlLabel-root {
      opacity: 0.7;
      pointer-events: none;
    }

    label,
    .MuiFormLabel-root,
    .MuiInputLabel-root {
      color: #6b6b6b !important;
    }
  `}
`;

const TwoCol = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 0 var(--space-6);
  align-items: start;
`;

const Col = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const ButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 24px;
  align-items: center;
  justify-content: center;

  .MuiButton-root {
    margin-top: 0;
  }
`;

const SubmitHelperText = styled.div`
  margin: 24px 0 0;
  font-size: 0.8125rem;
  color: var(--neutral-600);
  text-align: center;
  line-height: 1.4;

  & + ${ButtonRow} {
    margin-top: 12px;
  }
`;

const LabelWithHint = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: var(--mainText);
`;

const HintIconWrap = styled.div`
  display: inline-flex;
  align-items: center;
  color: var(--neutral-500);
  cursor: help;
  line-height: 0;

  &:hover,
  &:focus-visible {
    color: var(--primary-600);
    outline: none;
  }
`;

const SecondarySubmitBtn = styled.button`
  height: 40px;
  padding: 0 18px;
  border: 1px solid var(--primary-600);
  border-radius: 8px;
  background: var(--neutral-0);
  color: var(--primary-700);
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: background-color 0.15s ease;
  font-family: inherit;

  &:hover {
    background: var(--primary-50);
  }

  &:disabled {
    border-color: var(--neutral-200);
    color: var(--neutral-400);
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

const BackBtn = styled.button`
  height: 40px;
  padding: 0 18px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--neutral-0);
  color: var(--neutral-700);
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;
  font-family: inherit;

  &:hover {
    background: var(--neutral-50);
    border-color: var(--neutral-400);
  }

  &:disabled {
    color: var(--neutral-400);
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

interface Props {
  addButtonText?: string;
  updateButtonText?: string;
  initialValues?: UserTableType;
  onSubmit: (data: UserType) => void;
  initialErrors?: FormServerError<UserType>;
  modalError: boolean;
  setIsContactFormSubmitting?: (data) => void;
  cameFromSearch?:boolean;
  searchForm?: boolean;
  lockClientType?: boolean;
  secondarySubmitButton?: {
    label: string;
    onClick: (data: UserType) => void | Promise<void>;
  };
  submitHelperText?: string;
  companyMode?: boolean;
  onBack?: () => void;
  hideRoleAndType?: boolean;
  hideParentCompany?: boolean;
  // Resolve false to cancel the submit (e.g. a confirmation popup was declined).
  beforeSubmit?: (values: UserTableType) => Promise<boolean>;
}

export default UsersForm;
